import { getToken } from "@/utils/api"
import React, { forwardRef, useEffect, useState } from "react"
import { useObjectURL } from "../hooks/useObjectURL"
import {
  COOKIE_AUTH_ENABLED,
  getAuthFetchOptions,
} from "../generated/api/auth_cookies"
import Image from "next/image"

type ImageBaseProps = React.ComponentPropsWithoutRef<typeof Image>
type ImageProps = Omit<ImageBaseProps, "src" | "width" | "height"> &
  Required<Pick<ImageBaseProps, "width" | "height">> & {
    src: string
  }
type ImageWithAuthComponent = React.ForwardRefExoticComponent<
  ImageProps & React.RefAttributes<HTMLImageElement>
>
/**
 * On localhost, authorization does not work with cookies so request headers are used instead.
 * There is no way to send request headers with an <img> element.
 *
 * As a work-around, this component fetches it manually and stores it in a Blob.
 */
const ImageWithHeaderAuth: ImageWithAuthComponent = forwardRef(
  ({ src, ...props }, ref) => {
    const [imageBlob, setImageBlob] = useState<Blob | undefined>()

    useEffect(() => {
      fetch(src, getAuthFetchOptions(getToken()))
        .then((res) => res.blob())
        .then((blob) => setImageBlob(blob))
    }, [src])

    const blobSrc = useObjectURL(imageBlob)

    return <Image ref={ref} src={blobSrc} {...props} />
  },
)
ImageWithHeaderAuth.displayName = "ImageWithHeaderAuth"

/**
 * This component behaves a bit differently depending on whether cookie auth is enabled.
 * - Cookie auth: The "src" property points to the image download URL.
 * - Header auth: The "src" property points to a Blob object URL.
 *
 * See: {@link ImageWithHeaderAuth}
 */
export const ImageWithAuth = COOKIE_AUTH_ENABLED
  ? (Image as ImageWithAuthComponent)
  : ImageWithHeaderAuth
